/**
 * BlogPostCanvas: Renders a live preview of a blog post in the builder canvas.
 * Mirrors InlinePreview: full-width sections, dnd-kit drag-and-drop, drag handles,
 * site footer at bottom, SectionContextPanel-compatible interaction.
 *
 * Builder-created posts start with a hero section (headline = post title);
 * imported posts have no hero block and get a static header instead. Either
 * way the presentation is the shared BlogPostHeader (NOS-220): full-bleed
 * cover-image hero with overlaid title/meta, or a clean no-image header.
 */
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core"
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { BlockRenderer } from "./BlockRenderer"
import { PublicNavBar } from "@/components/PublicNavBar"
import { parseAnnouncement } from "@/lib/announcement"
import { BlogPostHeader } from "@/components/blog/BlogPostHeader"
import { calcReadingTime } from "@/lib/blog/postMeta"
import { useGoogleFont } from "@/hooks/useGoogleFont"
import { getSectionStyle } from "./sectionStyles"
import { getSectionLabel } from "./sectionLabels"
import type { OrgCustomizations, Organization, PageSection } from "@/templates/types"
import type { NavItem } from "@/types/navigation"

export interface BlogLiveData {
  title: string
  slug: string
  excerpt: string | null
  cover_image: string | null
  category_id: string | null
  category_name: string | null
  category_color: string | null
  is_published: boolean
  published_at: string | null
  updated_at: string | null
  blocks: PageSection[]
}

export interface BlogPostCanvasProps {
  liveData: BlogLiveData
  customizations: OrgCustomizations | null
  org: Organization | null
  navItems?: NavItem[]
  pages?: Array<{ id: string; slug: string; title: string | null }>
  selectedSectionId: string | null
  onSectionClick: (sectionId: string, e: React.MouseEvent) => void
  onAddBlockAt?: (afterIndex: number) => void
  onReorderBlocks?: (blocks: PageSection[]) => void
  footerSection?: PageSection | null
  isMobileViewport?: boolean
}

/** Blog context relabel: "heading" blocks act as the post body content. */
const BLOG_LABEL_OVERRIDES: Record<string, string> = { heading: "Content" }

/**
 * NOS-220: Shared blog post header (hero when cover_image exists, clean
 * variant otherwise) — same presentation as the public page and preview.
 * The canvas keeps its editing affordances around it.
 */
function BlogHeroContent({
  liveData,
  primaryColor,
}: {
  liveData: BlogLiveData
  primaryColor: string
}) {
  return (
    <BlogPostHeader
      title={liveData.title || "Untitled"}
      coverImage={liveData.cover_image}
      categoryName={liveData.category_name}
      categoryColor={liveData.category_color}
      publishedAt={liveData.published_at}
      updatedAt={liveData.updated_at}
      readingTimeMinutes={calcReadingTime(liveData.blocks)}
      primaryColor={primaryColor}
    />
  )
}

/**
 * Sortable canvas block — mirrors SortableSection in InlinePreview.
 * Shared by BlogPostCanvas and CampaignCanvas (NOS-260).
 */
export function SortableBlock({
  section,
  primaryColor,
  customizations,
  org,
  selectedSectionId,
  onSectionClick,
  customContent,
  styleOverride,
  labelOverrides,
}: {
  section: PageSection
  primaryColor: string
  customizations: OrgCustomizations
  org: Organization | null
  selectedSectionId: string | null
  onSectionClick: (sectionId: string, e: React.MouseEvent) => void
  /** Replaces BlockRenderer when provided */
  customContent?: React.ReactNode
  /** Overrides computed section style */
  styleOverride?: React.CSSProperties
  /** Per-context badge relabels (e.g. blog: heading → "Content") */
  labelOverrides?: Record<string, string>
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: section.id })

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  const content = (section.content as Record<string, unknown>) ?? null
  const isSelected = selectedSectionId === section.id
  const sectionStyle = { ...getSectionStyle(), ...styleOverride }
  const outlineColor = styleOverride ? primaryColor
    : section.section_type === "donation_cta" || section.section_type === "hero"
      ? "#fff"
      : primaryColor

  return (
    <div ref={setNodeRef} style={style} className="drag-zone" data-dragging={isDragging}>
      {/* Drag handle — ONLY gets listeners (click on body opens panel) */}
      <div
        {...attributes}
        {...listeners}
        style={{
          position: "absolute",
          left: -30,
          top: "50%",
          transform: "translateY(-50%)",
          color: "#fff",
          fontSize: 14,
          cursor: "grab",
          opacity: isDragging ? 1 : 0.5,
          transition: "opacity 0.15s",
          zIndex: 20,
          userSelect: "none",
          fontFamily: "'DM Sans', sans-serif",
        }}
        className="drag-h"
      >
        ⠿
      </div>

      <div
        role="button"
        tabIndex={0}
        onClick={(e) => onSectionClick(section.id, e)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ")
            onSectionClick(section.id, e as unknown as React.MouseEvent)
        }}
        style={{
          ...sectionStyle,
          outline: isSelected ? `2px solid ${outlineColor}` : "none",
          cursor: "pointer",
          opacity: isDragging ? 0.5 : 1,
          boxShadow: isDragging ? "0 20px 60px rgba(0,0,0,0.5)" : undefined,
        }}
      >
        {isSelected && (
          <div
            style={{
              position: "absolute",
              top: 10,
              left: 10,
              padding: "3px 8px",
              background:
                section.section_type === "donation_cta" || section.section_type === "hero"
                  ? "#fff"
                  : primaryColor,
              color:
                section.section_type === "donation_cta" || section.section_type === "hero"
                  ? primaryColor
                  : "#fff",
              fontSize: 9,
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              borderRadius: 5,
              zIndex: 10,
              fontFamily: "'DM Sans', sans-serif",
            }}
          >
            {getSectionLabel(section.section_type, content?._name as string | undefined, labelOverrides)}
          </div>
        )}
        {customContent ?? <BlockRenderer section={section} customizations={customizations} org={org} inBuilder />}
      </div>
    </div>
  )
}

/** Site footer section — locked, selectable. Shared by blog + campaign canvases (NOS-260). */
export function CanvasSiteFooter({
  footerSection,
  primaryColor,
  customizations,
  org,
  isSelected,
  onSectionClick,
}: {
  footerSection: PageSection
  primaryColor: string
  customizations: OrgCustomizations
  org: Organization | null
  isSelected: boolean
  onSectionClick: (sectionId: string, e: React.MouseEvent) => void
}) {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={(e) => onSectionClick(footerSection.id, e)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ")
          onSectionClick(footerSection.id, e as unknown as React.MouseEvent)
      }}
      style={{
        ...getSectionStyle(),
        outline: isSelected ? "2px solid #fff" : "none",
        cursor: "pointer",
      }}
    >
      {isSelected && (
        <div
          style={{
            position: "absolute",
            top: 10,
            left: 10,
            padding: "3px 8px",
            background: "#fff",
            color: primaryColor,
            fontSize: 9,
            fontWeight: 700,
            textTransform: "uppercase" as const,
            letterSpacing: "0.06em",
            borderRadius: 5,
            zIndex: 10,
            fontFamily: "'DM Sans', sans-serif",
          }}
        >
          Footer
        </div>
      )}
      <BlockRenderer
        section={footerSection}
        customizations={customizations}
        org={org}
        inBuilder
      />
    </div>
  )
}

/** Hover-reveal gap inserter between blog blocks */
function BlogGapInserter({
  afterIndex,
  primaryColor: _primaryColor,
  onAddBlockAt,
}: {
  afterIndex: number
  primaryColor: string
  onAddBlockAt: (afterIndex: number) => void
}) {
  return (
    <div
      className="blog-section-gap"
      style={{
        height: 0,
        position: "relative",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <button
        type="button"
        className="blog-gap-btn"
        onClick={(e) => {
          e.stopPropagation()
          onAddBlockAt(afterIndex)
        }}
        aria-label="Add section"
        style={{
          position: "absolute",
          top: 0,
          left: "50%",
          transform: "translate(-50%, -50%)",
          opacity: 0,
          padding: "4px 12px",
          borderRadius: 20,
          fontSize: 10,
          fontWeight: 600,
          zIndex: 20,
          cursor: "pointer",
          fontFamily: "'DM Sans', sans-serif",
          background: "#0f0f14",
          border: "1px solid #0f0f14",
          color: "#fff",
        }}
      >
        + Section
      </button>
      <style>{`
        .blog-section-gap:hover .blog-gap-btn {
          opacity: 1 !important;
          box-shadow: 0 6px 20px rgba(0,0,0,0.45), 0 0 0 1px rgba(0,0,0,0.08) !important;
        }
      `}</style>
    </div>
  )
}

export function BlogPostCanvas({
  liveData,
  customizations,
  org,
  navItems = [],
  pages = [],
  selectedSectionId,
  onSectionClick,
  onAddBlockAt,
  onReorderBlocks,
  footerSection,
  isMobileViewport = false,
}: BlogPostCanvasProps) {
  const fontFamily = customizations?.font_family ?? "Inter, sans-serif"
  const primaryColor = customizations?.primary_color ?? "#3b82f6"
  const secondaryColor = customizations?.secondary_color ?? "#1e40af"

  useGoogleFont(fontFamily)

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )

  const cssVars = {
    "--primary-color": primaryColor,
    "--secondary-color": secondaryColor,
    "--font-family": fontFamily,
  } as React.CSSProperties

  const sortedBlocks = [...liveData.blocks].sort(
    (a, b) => (a.order_index ?? 0) - (b.order_index ?? 0)
  )

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id || !onReorderBlocks) return
    const fromIdx = sortedBlocks.findIndex((b) => b.id === active.id)
    const toIdx = sortedBlocks.findIndex((b) => b.id === over.id)
    if (fromIdx === -1 || toIdx === -1) return
    const newBlocks = arrayMove(sortedBlocks, fromIdx, toIdx)
    onReorderBlocks(newBlocks)
  }

  if (!customizations) {
    return (
      <div
        style={{
          padding: 48,
          color: "#999",
          fontFamily: "'DM Sans', sans-serif",
          fontSize: 14,
        }}
      >
        Loading…
      </div>
    )
  }

  const isFooterSelected = footerSection ? selectedSectionId === footerSection.id : false

  // NOS-220: builder-created posts start with a hero block whose presentation
  // is replaced by the shared header (keeps drag/select affordances). Imported
  // posts have no hero block — render the shared header statically so the
  // canvas matches the public page.
  const hasLeadingHero = sortedBlocks[0]?.section_type === "hero"

  return (
    <div
      style={{
        all: "initial",
        // NOS-156: size container for the render layer's @sm/@md/@lg variants.
        containerType: "inline-size",
        fontFamily,
        display: "block",
        // NOS-151: section wrappers paint nothing, so the canvas root supplies
        // the page background (same as InlinePreview)
        background: "#ffffff",
        ...cssVars,
      }}
    >
      <PublicNavBar
        orgName={org?.name ?? "Organization"}
        logoUrl={org?.logo_url}
        navItems={navItems}
        pages={pages}
        primaryColor={primaryColor}
        navCtaText={customizations?.nav_cta_text ?? "Donate"}
        announcement={parseAnnouncement(customizations?.announcement)}
        fontFamily={fontFamily}
        isMobileViewport={isMobileViewport}
        selectable
        selectedSectionId={selectedSectionId}
        onSectionClick={onSectionClick}
        logoScale={(customizations?.logo_scale as number | undefined) ?? 1.0}
      />

      {/* NOS-220: no leading hero block (imported posts) — static shared header */}
      {!hasLeadingHero && (
        <div style={{ background: "#fff" }}>
          <BlogHeroContent liveData={liveData} primaryColor={primaryColor} />
        </div>
      )}

      {/* Content sections — full-width, sortable, same pattern as InlinePreview */}
      {sortedBlocks.length === 0 ? (
        <div style={{ background: "#fff", textAlign: "center", padding: "48px 0" }}>
          {onAddBlockAt ? (
            <button
              type="button"
              onClick={() => onAddBlockAt(-1)}
              style={{
                padding: "10px 20px",
                background: primaryColor,
                border: "none",
                borderRadius: 8,
                color: "#fff",
                fontSize: 13,
                fontWeight: 500,
                cursor: "pointer",
                fontFamily: "'DM Sans', sans-serif",
              }}
            >
              + Add first section
            </button>
          ) : (
            <p style={{ color: "#94a3b8", fontSize: 14, fontFamily: "'DM Sans', sans-serif" }}>
              No content sections yet. Add sections in the panel on the left.
            </p>
          )}
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={sortedBlocks.map((b) => b.id)}
            strategy={verticalListSortingStrategy}
          >
            {sortedBlocks.map((block, idx) => (
              <div key={block.id} style={{ position: "relative" }}>
                {onAddBlockAt && (
                  <BlogGapInserter
                    afterIndex={idx - 1}
                    primaryColor={primaryColor}
                    onAddBlockAt={onAddBlockAt}
                  />
                )}
                <SortableBlock
                  section={block}
                  primaryColor={primaryColor}
                  customizations={customizations}
                  org={org}
                  selectedSectionId={selectedSectionId}
                  onSectionClick={onSectionClick}
                  labelOverrides={BLOG_LABEL_OVERRIDES}
                  customContent={
                    idx === 0 && block.section_type === "hero" ? (
                      <BlogHeroContent liveData={liveData} primaryColor={primaryColor} />
                    ) : undefined
                  }
                  styleOverride={
                    idx === 0 && block.section_type === "hero"
                      ? { background: "#fff" }
                      : undefined
                  }
                />
              </div>
            ))}
          </SortableContext>
        </DndContext>
      )}

      {/* Add section at end */}
      {onAddBlockAt && sortedBlocks.length > 0 && (
        <BlogGapInserter
          afterIndex={sortedBlocks.length - 1}
          primaryColor={primaryColor}
          onAddBlockAt={onAddBlockAt}
        />
      )}

      {/* Site footer — locked, selectable */}
      {footerSection && (
        <CanvasSiteFooter
          footerSection={footerSection}
          primaryColor={primaryColor}
          customizations={customizations}
          org={org}
          isSelected={isFooterSelected}
          onSectionClick={onSectionClick}
        />
      )}
    </div>
  )
}
